import {Switch, Route, Redirect, useLocation} from 'react-router-dom';
import cn from 'classnames';

import Sidebar from 'components/Sidebar';

import Support from 'pages/Support';
import DeliveryPlanner from 'pages/DeliveryPlanner';
import InstorePickups from 'pages/InstorePickups';
import Deliveries from 'pages/Deliveries';
import Settings from 'pages/Settings';
import RequestDetails from 'pages/RequestDetails';
import Requests from 'pages/Requests';
import EmployeeForm from 'pages/EmployeeForm';
import VehicleForm from 'pages/VehicleForm';
import UserDetails from 'pages/UserDetails';
import UserForm from 'pages/UserForm';
import Routes from 'pages/Settings/Routes';
import {CustomRouteProps} from 'components/CustomRoute';
import useUi from 'hooks/useUi';
// import Products from 'pages/Products';
import {Toaster} from 'react-hot-toast';
import RouteHistory from 'pages/RouteHistory';
import Transactions from 'pages/Transactions';
import TransactionDetails from 'pages/TransactionDetails';
import DoctorSettings from 'pages/PrescriberSettings';
import DoctorPresciptions from 'pages/PrescriberPrescriptions';
import AddPresscription from 'pages/AddPresscription';
import useAuth from 'hooks/useAuth';
import {get} from 'lodash';
import PrescriberUsers from 'pages/PrescriberUsers';
import {IntercomProvider, useIntercom} from 'react-use-intercom';
import {useEffect} from 'react';
import Website from 'pages/Website';
import Users from 'pages/Settings/Users';
import Prescribers from 'pages/Settings/Prescribers';
import Account from 'pages/Account';

const INTERCOM_APP_ID = 'nnus1b58';

const mainRoutes: CustomRouteProps[] = [
  {path: '/account', component: Account, exact: true, route: 'account'},
  {path: '/support', component: Support, exact: true, route: 'support'},
  {
    path: '/prescriber/settings',
    component: DoctorSettings,
    route: 'prescribers',
  },
  {
    path: '/prescriber/prescriptions',
    exact: true,
    component: () => <DoctorPresciptions source="micromerchant" />,
    route: 'prescribers',
  },
  {
    path: '/prescriber/prescriptions-create',
    component: AddPresscription,
    route: 'prescribers',
  },
  {path: '/prescriber/users', component: PrescriberUsers, route: 'users'},
  {
    path: '/support/page/:page',
    component: Support,
    exact: true,
    route: 'support',
  },
  {
    path: '/deliveries/delivery_planner',
    component: DeliveryPlanner,
    route: 'dispatch',
  },
  {path: '/deliveries/in-store_pickups', component: InstorePickups},
  {path: '/deliveries', component: Deliveries},
  // {path: '/products/page/:page', component: Products},
  // {path: '/products', component: Products},
  {
    path: '/settings/employees/new',
    component: EmployeeForm,
    route: 'employees',
  },
  {
    path: '/settings/employees/:employeeId',
    component: EmployeeForm,
    route: 'employees',
  },
  {path: '/settings/vehicles/new', component: VehicleForm, route: 'vehicles'},
  {
    path: '/settings/vehicles/:vehicleId',
    component: VehicleForm,
    route: 'vehicles',
  },
  {path: '/users', component: Users, exact: true, route: 'patients'},
  {
    path: '/prescribers',
    component: Prescribers,
    exact: true,
    route: 'prescribers',
  },
  {path: '/users/:userId/edit', component: UserForm, route: 'patients'},
  {path: '/users/:userId', component: UserDetails, route: 'patients'},
  {
    path: '/prescribers/:employeeId',
    component: EmployeeForm,
    route: 'prescribers',
  },
  {path: '/settings', component: Settings, route: 'settings'},
  {
    path: '/orders/:id',
    component: RequestDetails,
    exact: true,
    route: 'requests',
  },
  {
    path: '/requests/:id',
    component: RequestDetails,
    exact: true,
    route: 'requests',
  },
  {
    path: '/transactions/:id',
    component: TransactionDetails,
    exact: true,
    route: 'transactions',
  },
  {
    path: '/requests/page/:page',
    component: () => <Requests source="app" />,
    exact: true,
    route: 'requests',
  },
  {
    path: '/orders/page/:page',
    component: () => <Requests source="micromerchant" />,
    exact: true,
    route: 'requests',
  },
  {
    path: '/requests',
    component: () => <Requests source="app" />,
    exact: true,
    route: 'requests',
  },
  {
    path: '/transactions',
    component: () => <Transactions />,
    exact: true,
    route: 'transactions',
  },
  {
    path: '/transactions/page/:page',
    component: () => <Transactions />,
    exact: true,
    route: 'transactions',
  },
  {
    path: '/orders',
    component: () => <Requests source="micromerchant" />,
    exact: true,
    route: 'requests',
  },
  {
    path: '/history/routes/page/:page',
    component: Routes,
    exact: true,
    route: 'dispatch',
  },
  {
    path: '/history/routes/:id',
    component: RouteHistory,
    exact: true,
    route: 'dispatch',
  },
  {path: '/website', component: Website, route: 'website'},
];

function MainApp() {
  const {sidebarExtended} = useUi();
  const {pathname} = useLocation();
  const {currentUser, rolePermission} = useAuth();
  return (
    <>
      <Sidebar />
      <IntercomProvider appId={INTERCOM_APP_ID}>
        <div
          className={cn('content', {
            '--sidebar-extended': sidebarExtended,
            '--fullsize':
              pathname.startsWith('/deliveries') ||
              pathname.startsWith('/history/routes'),
          })}
        >
          <Switch>
            {mainRoutes
              .filter((item) => {
                const permission = rolePermission[item.route as string];

                if (!!permission) {
                  return permission.permissions.read;
                }
                return true;
              })
              .map((route) => (
                <Route key={route.path} {...route} />
              ))}
            <Redirect
              to={
                get(currentUser, 'role') === 'prescriber'
                  ? '/prescriber/prescriptions-create'
                  : rolePermission['requests']?.permissions?.read
                  ? '/requests'
                  : '/account'
              }
            />
          </Switch>
        </div>
        <IntercomChat />
      </IntercomProvider>
      <Toaster />
    </>
  );
}

const IntercomChat = () => {
  const {currentUser} = useAuth();
  const {boot} = useIntercom();

  useEffect(() => {
    if (get(currentUser, 'role') === 'prescriber') {
      boot();
    }
  }, [currentUser, boot]);

  return null;
};

export default MainApp;
